import { NotificationContextType, NotificationMessage } from "@/contexts/notifications";
import { useMemo, useCallback, useRef } from "react";
import { useDarkMode } from "./useDarkMode";

/**
 * Lazy notification API — loads react-toastify on first notification.
 * Keeps the ~20KB toast library out of the initial bundle.
 */
export function useLazyNotificationToastApi(): NotificationContextType {
  const [mode] = useDarkMode();
  const toastModuleRef = useRef<typeof import("react-toastify") | null>(null);
  const cssLoadedRef = useRef(false);

  const showNotification = useCallback(async (notification: NotificationMessage) => {
    // Lazy load react-toastify + CSS on first call
    if (!toastModuleRef.current) {
      toastModuleRef.current = await import("react-toastify");
    }
    if (!cssLoadedRef.current) {
      await import("react-toastify/dist/ReactToastify.css");
      cssLoadedRef.current = true;
    }

    const { toast } = toastModuleRef.current;

    const toastId = toast(<div
      className="flex flex-col rounded p-2"
    >
      {notification.title && <div className="flex flex-row gap-2">
        {notification.icon &&
          <div className="flex flex-col items-center justify-center min-h-min w-auto">
            {notification.icon}
          </div>
        }
        <div className="grow shrink font-bold text-sm">
          {notification.title}
        </div>
      </div>}
      <div className="flex flex-row gap-2">
        {notification.icon &&
          <div className="flex flex-col items-center justify-center min-h-min w-auto">
            {notification.icon}
          </div>
        }
        <div className="grow shrink flex flex-col gap-2">
          <div className="grow shrink">
            {notification.message}
          </div>
        </div>
      </div>
      {notification.actions &&
        <div className="flex flex-row gap-2">
          {notification.actions.map(action => {
            if (typeof action === "function") {
              return action({
                close: () => toast.dismiss(toastId)
              })
            }

            return action;
          })}
        </div>}

    </div>, {
      toastId: notification.id,
      autoClose: notification.actions ? false : (notification.timeout || (notification.status === "critical" ? 5000 : 3000)),
      theme: mode
    });
  }, [mode]);

  return useMemo(() => ({ showNotification }), [showNotification]);
}

/**
 * @deprecated Use useLazyNotificationToastApi instead
 */
export const useNotificationToastApi = useLazyNotificationToastApi;
